import { useState, useRef } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Upload, X, Check, AlertCircle } from 'lucide-react';
import { useModelsStore } from '@/stores/models-store';
import { useUIStore } from '@/stores/ui-store';
import { useNotificationsStore } from '@/stores/notifications-store';
import Button from '@/components/ui/Button';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_EXTENSIONS = ['.glb', '.gltf'];

export default function UploadModelDrawer() {
  const { uploadModel, isUploadingModel } = useModelsStore();
  const { setUploadDrawerOpen, showToast } = useUIStore();
  const { notifyModelUploaded } = useNotificationsStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleClose = () => {
    setUploadDrawerOpen(false);
  };

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`;
    }

    const fileName = file.name.toLowerCase();
    const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
    if (!hasValidExtension) {
      return `Invalid file type. Only ${ALLOWED_EXTENSIONS.join(', ')} files are allowed`;
    }

    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
    setError(null);

    if (!name) {
      const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
      setName(fileNameWithoutExt);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();

    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    if (!name.trim()) {
      setError('Please enter a model name');
      return;
    }

    try {
      const uploadedModel = await uploadModel(selectedFile, name.trim(), description.trim() || undefined, isPublic);

      // Notify success
      notifyModelUploaded(uploadedModel.name, uploadedModel.id);
      showToast('Model uploaded successfully!', 'success');

      // Reset form and close drawer
      setSelectedFile(null);
      setName('');
      setDescription('');
      setIsPublic(false);
      setError(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload model';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: '100%' }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="fixed inset-0 bg-background z-[100] overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex-shrink-0 safe-area-top">
        <div className="flex items-center justify-between p-4 pt-12">
          <button
            onClick={handleClose}
            className="w-12 h-12 rounded-full glass-dark flex items-center justify-center touch-feedback"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-semibold">Upload Model</h1>
          <div className="w-12" /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto no-scrollbar px-6 pb-32">
        <form id="upload-form" onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-medium mb-3 text-white/70">3D Model File</label>
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-3xl p-8 transition-colors ${
                dragActive
                  ? 'border-accent-blue bg-accent-blue/10'
                  : 'border-white/20 hover:border-white/40'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".glb,.gltf,model/gltf-binary,model/gltf+json,application/octet-stream,*/*"
                onChange={handleFileInputChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                disabled={isUploadingModel}
                onClick={(e) => e.stopPropagation()}
              />

              {selectedFile ? (
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-accent-blue/20 flex items-center justify-center">
                    <Check size={28} className="text-accent-blue" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{selectedFile.name}</p>
                    <p className="text-white/50 text-sm">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="w-10 h-10 rounded-full glass-dark flex items-center justify-center hover:bg-white/10 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div
                  className="text-center"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                    <Upload size={28} className="text-white/50" />
                  </div>
                  <p className="font-medium mb-1">Drop your file here</p>
                  <p className="text-white/50 text-sm mb-2">or tap to browse</p>
                  <p className="text-white/30 text-xs">
                    .glb, .gltf files up to 50MB
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Model Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-3 text-white/70">
              Model Name *
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter model name"
              maxLength={100}
              disabled={isUploadingModel}
              className="w-full px-5 py-4 rounded-2xl glass border border-white/10 focus:border-accent-blue focus:outline-none transition-colors text-white placeholder:text-white/30"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-3 text-white/70">
              Description (Optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description for your model"
              maxLength={500}
              rows={4}
              disabled={isUploadingModel}
              className="w-full px-5 py-4 rounded-2xl glass border border-white/10 focus:border-accent-blue focus:outline-none transition-colors resize-none text-white placeholder:text-white/30"
            />
            <p className="text-white/30 text-xs mt-2 text-right">
              {description.length}/500
            </p>
          </div>

          {/* Public Toggle */}
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Make Public</p>
                <p className="text-white/50 text-sm mt-1">
                  Allow others to view this model
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsPublic(!isPublic)}
                disabled={isUploadingModel}
                className={`relative w-14 h-8 rounded-full transition-colors ${
                  isPublic ? 'bg-accent-blue' : 'bg-white/20'
                }`}
              >
                <div
                  className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${
                    isPublic ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20"
            >
              <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </motion.div>
          )}
        </form>
      </div>

      {/* Bottom CTA */}
      <div className="absolute bottom-0 left-0 right-0 p-6 pb-8 safe-area-bottom bg-gradient-to-t from-background via-background to-transparent">
        <Button
          onClick={handleSubmit}
          fullWidth
          size="lg"
          disabled={isUploadingModel || !selectedFile || !name.trim()}
          loading={isUploadingModel}
        >
          Upload Model
        </Button>
      </div>
    </motion.div>
  );
}
